import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, getTrySessionId, API } from '@/lib/api';
import { AgentAvatar } from '@/components/AgentAvatar';
import { Send, ArrowLeft, Sparkles, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { UpgradeModal } from '@/components/UpgradeModal';

export default function Share() {
  const { id } = useParams();
  const trySessionId = useMemo(() => getTrySessionId(), []);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // local-only display for this share session
  const [streamingText, setStreamingText] = useState('');
  const [sending, setSending] = useState(false);
  const [anonRemaining, setAnonRemaining] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const endRef = useRef(null);

  const { data: agent, isLoading, isError } = useQuery({
    queryKey: ['share-agent', id],
    queryFn: async () => (await api.get(`/share/${id}`)).data,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const onSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending || !agent?.agent_id) return;
    setSending(true);
    setStreamingText('');
    const userText = text;
    setInput('');
    setMessages((prev) => [...prev, { message_id: `local_u_${Date.now()}`, role: 'user', content: userText }]);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: agent.agent_id,
          message: userText,
          conversation_id: conversationId,
          try_session_id: trySessionId,
        }),
      });
      if (resp.status === 402) {
        setShowUpgrade(true);
        setSending(false);
        return;
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const raw = line.replace(/^data:\s*/, '');
          let evt;
          try { evt = JSON.parse(raw); } catch { continue; }
          if (evt.type === 'start') {
            if (!conversationId) setConversationId(evt.conversation_id);
          } else if (evt.type === 'chunk') {
            acc += evt.content || '';
            setStreamingText(acc);
          } else if (evt.type === 'done') {
            setMessages((prev) => [...prev, { message_id: `local_a_${Date.now()}`, role: 'assistant', content: acc }]);
            setStreamingText('');
            if (typeof evt.anon_remaining === 'number') setAnonRemaining(evt.anon_remaining);
          } else if (evt.type === 'error') {
            toast.error(evt.detail || 'LLM error');
          }
        }
      }
    } catch (err) {
      toast.error(err?.message || 'Streaming failed');
    } finally {
      setSending(false);
    }
  };

  if (isLoading) return <div className="max-w-3xl mx-auto pt-12 px-4 text-white/60">Loading…</div>;
  if (isError || !agent) return (
    <div className="max-w-3xl mx-auto pt-12 px-4 text-center">
      <div className="text-white/60">This agent isn't shared publicly.</div>
      <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium mt-4">Browse marketplace</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        isAnonymous={true}
        trySessionId={trySessionId}
      />
      <Link to="/marketplace" className="btn-ghost inline-flex items-center gap-1 text-xs px-2 py-1 rounded">
        <ArrowLeft className="h-3.5 w-3.5" /> See more agents
      </Link>

      <div className="mt-4 card-base p-5 flex items-center gap-4">
        <AgentAvatar name={agent.name} size={56} />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">{agent.category}</span>
          <div className="display text-xl font-semibold">{agent.name}</div>
          <div className="text-xs text-white/55 truncate">{agent.role} · <span className="mono">{agent.model_provider}/{agent.model_name}</span></div>
        </div>
        <div className="hidden sm:flex flex-col items-end">
          <span className="text-[11px] hairline px-2 py-1 rounded-full text-white/65 inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-[#FF6B1A]" /> Shared via RevCore AI
          </span>
          <span className="text-[10px] text-white/40 mt-1">{anonRemaining ?? 5} free messages</span>
        </div>
      </div>

      <div className="mt-4 card-base flex flex-col h-[60vh] min-h-[440px] overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !streamingText && (
            <div className="h-full grid place-items-center text-center text-white/55">
              <div>
                <p className="display text-lg">Say hi to {agent.name} 👋</p>
                <p className="text-sm mt-1">Free preview. Try 5 messages, then sign up to unlock more.</p>
              </div>
            </div>
          )}
          {messages.map((m) => (
            <motion.div key={m.message_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 max-w-2xl ${m.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}>
              {m.role === 'assistant' ? <AgentAvatar name={agent.name} size={28} /> : <div className="h-7 w-7 rounded-lg bg-white/5 grid place-items-center text-[10px] text-white/70 mono">YOU</div>}
              <div className={`p-3 rounded-xl border text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-white/5 border-white/8' : 'bg-card/60 border-border/60'
              }`}>{m.content}</div>
            </motion.div>
          ))}
          {streamingText && (
            <div className="flex gap-3 max-w-2xl mr-auto">
              <AgentAvatar name={agent.name} size={28} />
              <div className="p-3 rounded-xl border border-border/60 bg-card/60 text-sm whitespace-pre-wrap">
                {streamingText}<span className="inline-block w-2 h-4 bg-[#FF6B1A] ml-0.5 align-middle animate-pulse" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={onSend} className="p-3 border-t border-border/60 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Ask ${agent.name} anything…`}
            className="input flex-1"
          />
          <button type="submit" disabled={!input.trim() || sending}
            className="btn-primary rounded-xl px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
            <Send className="h-4 w-4"/> Send
          </button>
        </form>
      </div>

      <div className="mt-5 card-base p-5 text-center">
        <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-white/45">
          <AlertTriangle className="h-3 w-3 text-[#FF6B1A]" /> free preview · powered by RevCore AI
        </div>
        <p className="text-sm text-white/70 mt-2">Like what you see? Sign up free to save conversations and build your own.</p>
        <div className="mt-3 flex justify-center gap-2">
          <Link to="/login" className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">Sign up free</Link>
          <Link to="/marketplace" className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm">See all agents</Link>
        </div>
      </div>
    </div>
  );
}
